import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Menu, MenuItem, Typography, Divider, ListItemText, Tooltip, TextField, InputAdornment, Box, ListItemIcon
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  CalendarToday, Schedule, Event, Today, AccessTime, Folder, Description,
  Fingerprint, Assignment, TextSnippet, SummarizeOutlined, CreateNewFolder,
  EditNote, CheckCircle, Update, Extension, Person, LocationOn, AttachMoney,
  Message, Numbers, Flag, Category, LocalOffer, Image
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useActionScope } from './Actions/ActionScopeContext';
import { useDashboard } from './Dashboard/DashboardContext';
import { systemTagsEvent, systemTagsGlobal } from '../constants/systemTags';
import { buildReservedTagSet } from '../Utils/tagUtils';

const TagsDropdown = ({
  tags,
  handleTagSelect,
  onMissingTagSelect,
  anchorEl,
  setAnchorEl,
  additionalTags = [],
  scope
}) => {
  const { t } = useTranslation();
  const dashboard = useDashboard();
  const dashboardTags = dashboard?.tags;
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedTagValue, setHighlightedTagValue] = useState(null);
  const searchInputRef = useRef(null);
  const tooltipCacheRef = useRef(new Map()); // Cache tooltip content so we don't recompute on every hover
  const contextScope = useActionScope();
  const effectiveScope = scope || contextScope || 'event';
  const hideEventTags = effectiveScope === 'project';

  const focusSearchInput = useCallback(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus?.();
    });
  }, []);

  const resetSearchState = useCallback(() => {
    setSearchQuery('');
    setHighlightedTagValue(null);
  }, []);

  // Icon mapping function for string-based icon names from backend
  const getIconComponent = (iconName) => {
    const iconMap = {
      // Material-UI icon component names
      'CalendarToday': CalendarToday,
      'Schedule': Schedule,
      'Event': Event,
      'Today': Today,
      'AccessTime': AccessTime,
      'Folder': Folder,
      'Description': Description,
      'Fingerprint': Fingerprint,
      'Assignment': Assignment,
      'TextSnippet': TextSnippet,
      'SummarizeOutlined': SummarizeOutlined,
      'CreateNewFolder': CreateNewFolder,
      'EditNote': EditNote,
      'CheckCircle': CheckCircle,
      'Update': Update,
      'Extension': Extension,
      'Person': Person,
      'LocationOn': LocationOn,
      'AttachMoney': AttachMoney,
      'Message': Message,
      'Numbers': Numbers,
      'Flag': Flag,
      'Category': Category,
      'LocalOffer': LocalOffer,
      'Image': Image,
      // File-based icon mappings from backend
      'doctor.png': Person,
      'nurse.png': Person,
      'sparkling.png': SummarizeOutlined,
      'licence.png': Assignment,
      'validation.png': CheckCircle,
      'document.svg': Description,
      'document.png': Description
    };
    return iconMap[iconName] || LocalOffer; // Default to LocalOffer if icon not found
  };

  const getTagValue = useCallback((tag) => {
    const valueToUse = tag?.variable || tag?.value || tag?.key || tag?.name || tag?.label || tag;
    return typeof valueToUse === 'string' ? valueToUse.trim() : "";
  }, []);

  // Function to get icon for custom tags
  const getCustomTagIcon = (tag) => {
    // If the tag has an icon property (like from TimeEventTag), use it
    if (tag?.icon) {
      // Handle both string and component icons
      if (typeof tag.icon === 'string') {
        return getIconComponent(tag.icon);
      } else {
        return tag.icon;
      }
    }
    
    // Check for image type tags first
    if (tag?.type === 'image') {
      return Image;
    }
    
    // Fallback icon based on tag properties or content
    const value = getTagValue(tag).toLowerCase();
    const description = (tag?.description || '').toLowerCase();
    
    // Try to match by common patterns
    if (value.includes('date') || value.includes('time') || description.includes('date') || description.includes('time')) {
      return CalendarToday;
    }
    if (value.includes('file') || description.includes('file')) {
      return Description;
    }
    if (value.includes('id') || description.includes('identifier')) {
      return Fingerprint;
    }
    if (value.includes('text') || description.includes('text')) {
      return TextSnippet;
    }
    if (value.includes('image') || description.includes('image')) {
      return Image;
    }
    
    // Default icon for custom tags
    return LocalOffer;
  };
  const systemTags = useMemo(() => {
    if (hideEventTags) {
      return [...systemTagsGlobal];
    }
    return [
      ...systemTagsGlobal,
      ...systemTagsEvent
    ];
  }, [hideEventTags, systemTagsEvent, systemTagsGlobal]);

  const reservedTagValues = useMemo(
    () => buildReservedTagSet(systemTagsEvent, systemTagsGlobal),
    [systemTagsEvent, systemTagsGlobal]
  );

  const defaultTags = useMemo(() => [
    ...systemTags,
    ...additionalTags
  ], [systemTags, additionalTags]);

  const buildTooltipContent = useCallback((tag, displayLabel, currentValue) => {
    const description = tag?.description || '';
    const valueText = currentValue ? String(currentValue) : t('tags_dropdown.empty_value', 'No current value');
    if (!description) {
      return `${t('tags_dropdown.current_value', 'Current value')}: ${valueText}`;
    }
    return `${description}\n${t('tags_dropdown.current_value', 'Current value')}: ${valueText}`;
  }, [t]);

  const getTooltipContent = useCallback((tag, displayLabel) => {
    // Use the tag value/variable as cache key to avoid recomputing on hover
    const cacheKey = tag?.value || tag?.variable || tag?.label || displayLabel;
    if (cacheKey && tooltipCacheRef.current.has(cacheKey)) {
      return tooltipCacheRef.current.get(cacheKey);
    }

    const tagValueRaw = tag?.reply ?? tag?.current_value ?? tag?.currentValue ?? tag?.value ?? tag?.variable ?? '';
    const tooltipText = buildTooltipContent(tag, displayLabel, tagValueRaw);

    if (cacheKey) {
      tooltipCacheRef.current.set(cacheKey, tooltipText);
    }
    return tooltipText;
  }, [buildTooltipContent]);


  const handleClose = () => {
    if (setAnchorEl) { // Ensure setAnchorEl is a function before calling
        setAnchorEl(null);
    }
  };

  useEffect(() => {
    if (!anchorEl) {
      resetSearchState();
    }
  }, [anchorEl, resetSearchState]);

  const normalizeTagsInput = useCallback((tagsInput) => {
    if (!tagsInput) {
      return [];
    }
    if (typeof tagsInput === 'string') {
      try {
        const parsed = JSON.parse(tagsInput);
        return Array.isArray(parsed) ? parsed : [parsed];
      } catch (e) {
        return [tagsInput];
      }
    }
    if (Array.isArray(tagsInput)) {
      return tagsInput;
    }
    if (typeof tagsInput === 'object' && tagsInput !== null) {
      return [tagsInput];
    }
    return [];
  }, []);

  const safeTags = useMemo(() => {
    const merged = [];
    const seenValues = new Set();

    [tags, dashboardTags].forEach((tagGroup) => {
      normalizeTagsInput(tagGroup).forEach((tag) => {
        const normalizedValue = getTagValue(tag).toLowerCase();

        if (normalizedValue) {
          if (seenValues.has(normalizedValue)) {
            return;
          }
          seenValues.add(normalizedValue);
        }

        merged.push(tag);
      });
    });

    return merged;
  }, [tags, dashboardTags, normalizeTagsInput, getTagValue]);

  // Categorize tags based on tag_type field
  const { timeeventTags, widgetTags } = useMemo(() => {
    const timeevent = [];
    const widget = [];
    
    safeTags.forEach(tag => {
      const normalizedValue = getTagValue(tag).toLowerCase();
      if (!normalizedValue || reservedTagValues.has(normalizedValue)) {
        return;
      }

      if (tag.tag_type === 'widget') {
        widget.push(tag);
      } else {
        // Default to timeevent for backward compatibility
        timeevent.push(tag);
      }
    });
    
    return { timeeventTags: timeevent, widgetTags: widget };
  }, [safeTags, getTagValue, reservedTagValues]);

  const filteredTimeeventTags = useMemo(() => {
    if (hideEventTags) {
      return [];
    }
    if (!searchQuery.trim()) {
      return timeeventTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return timeeventTags.filter(tag => {
      const value = String(getTagValue(tag)).toLowerCase();
      const label = String(tag?.label || value).toLowerCase();
      const description = String(tag?.description || "").toLowerCase();
      return label.includes(query) ||
        value.includes(query) ||
        description.includes(query);
    });
  }, [timeeventTags, searchQuery, getTagValue, hideEventTags]);

  const filteredWidgetTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return widgetTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return widgetTags.filter(tag => {
      const value = String(getTagValue(tag)).toLowerCase();
      const label = String(tag?.label || value).toLowerCase();
      const description = String(tag?.description || "").toLowerCase();
      return label.includes(query) ||
        value.includes(query) ||
        description.includes(query);
    });
  }, [widgetTags, searchQuery, getTagValue]);

  const filteredDefaultTags = useMemo(() => {
    if (!searchQuery.trim()) {
      return defaultTags;
    }
    const query = searchQuery.trim().toLowerCase();
    return defaultTags.filter(tag => {
      const label = (tag?.label || '').toLowerCase();
      const value = (tag?.value || '').toLowerCase();
      const description = (tag?.description || '').toLowerCase();
      
      return label.includes(query) ||
        value.includes(query) ||
        description.includes(query);
    });
  }, [defaultTags, searchQuery]);

  const visibleTagOptions = useMemo(() => {
    const options = [];
    const seenValues = new Set();
    const pushOption = (tag, group) => {
      const value = getTagValue(tag).trim();
      if (!value) {
        return;
      }
      const normalizedValue = value.toLowerCase();
      if (seenValues.has(normalizedValue)) {
        return;
      }
      seenValues.add(normalizedValue);
      options.push({ tag, value, group });
    };

    filteredTimeeventTags.forEach((tag) => pushOption(tag, 'timeevent'));
    filteredWidgetTags.forEach((tag) => pushOption(tag, 'widget'));

    filteredDefaultTags.filter((tag) => ['projectid', 'project_title', 'project_description', 'username'].includes((tag?.variable || tag?.value || '').toString())).forEach((tag) => pushOption(tag, 'project'));
    filteredDefaultTags.filter((tag) => !['projectid', 'project_title', 'project_description', 'username'].includes((tag?.variable || tag?.value || '').toString())
      && !['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes((tag?.variable || tag?.value || '').toString())
      && ![
        'year', 'month', 'day', 'hour', 'minute', 'second',
        'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
        'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond',
        'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond'
      ].includes((tag?.variable || tag?.value || '').toString()))
      .forEach((tag) => pushOption(tag, 'project_other'));
    filteredDefaultTags.filter((tag) => ['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes((tag?.variable || tag?.value || '').toString())).forEach((tag) => pushOption(tag, 'file'));
    filteredDefaultTags.filter((tag) => [
      'year', 'month', 'day', 'hour', 'minute', 'second',
      'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
      'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond',
      'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond'
    ].includes((tag?.variable || tag?.value || '').toString())).forEach((tag) => pushOption(tag, 'datetime'));

    return options;
  }, [filteredDefaultTags, filteredTimeeventTags, filteredWidgetTags, getTagValue]);

  const handleSearchKeyDown = useCallback((event) => {
    event.stopPropagation();

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      if (visibleTagOptions.length === 0) {
        return;
      }

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const currentIndex = highlightedTagValue
        ? visibleTagOptions.findIndex((option) => option.value.toLowerCase() === String(highlightedTagValue || '').toLowerCase())
        : -1;
      const nextIndex = currentIndex === -1
        ? (direction > 0 ? 0 : visibleTagOptions.length - 1)
        : (currentIndex + direction + visibleTagOptions.length) % visibleTagOptions.length;
      setHighlightedTagValue(visibleTagOptions[nextIndex]?.value || null);
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    const normalizedHighlightedValue = String(highlightedTagValue || '').toLowerCase();
    const selectedOption = visibleTagOptions.find((option) => option.value.toLowerCase() === normalizedHighlightedValue);
    if (selectedOption?.value) {
      handleTagSelect(selectedOption.value);
      handleClose();
      return;
    }

    const normalizedQuery = searchQuery.trim().toLowerCase();
    const exactMatchOption = normalizedQuery
      ? visibleTagOptions.find((option) => (
          option.value.toLowerCase() === normalizedQuery ||
          String(option.tag?.label || '').toLowerCase() === normalizedQuery
        ))
      : null;
    if (exactMatchOption?.value) {
      handleTagSelect(exactMatchOption.value);
      handleClose();
      return;
    }

    const query = searchQuery.trim();
    if (query && onMissingTagSelect) {
      onMissingTagSelect(query);
      handleClose();
    }
  }, [handleClose, handleTagSelect, highlightedTagValue, onMissingTagSelect, searchQuery, visibleTagOptions]);


  
  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose} // This will call the setAnchorEl(null) from the parent
      autoFocus={false} // Keep this to prevent Menu itself from grabbing initial focus
      disableAutoFocusItem={true} // Keep this to prevent menu items from stealing focus
      disableEnforceFocus={true} // Allow focus to remain on the search input
      disableRestoreFocus={true} // Prevent focus restoration when menu closes
      TransitionProps={{
        onEntered: focusSearchInput
      }}
      MenuListProps={{
        onMouseDown: (e) => {
          // Prevent menu from closing when clicking inside
          if (e.target.closest('.search-container')) {
            e.preventDefault();
            e.stopPropagation();
          }
        }
      }}
      PaperProps={{
        elevation: 3,
        onMouseDown: (e) => {
          // Prevent default behavior for clicks on the search area
          if (e.target.closest('.search-container')) {
            e.preventDefault();
          }
        },
        sx: {
          width: 320,
          maxHeight: 450,
          borderRadius: '8px',
          mt: 1,
          overflow: 'auto',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f5f5f5' },
        },
      }}
    >
      <Box 
        className="search-container"
        sx={{ px: 2.5, py: 1.5, position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.preventDefault()}
      >
        <TextField
          inputRef={searchInputRef}
          variant="outlined"
          placeholder={t('auto_create_file.search_tags') || "Search tags..."}
          fullWidth
          size="small"
          value={searchQuery}
          onChange={(e) => {
            e.stopPropagation();
            setSearchQuery(e.target.value);
            setHighlightedTagValue(null);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            // Ensure focus stays on the input
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDownCapture={handleSearchKeyDown}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: '#666' }} />
              </InputAdornment>
            ),
            sx: { borderRadius: '8px', fontSize: '0.9rem' }
          }}
          sx={{
            '& .MuiInputBase-input': {
              color: '#111',
              caretColor: '#111'
            }
          }}
        />
      </Box>

      {/* CUSTOM TAGS (TimeEvent) - Now displayed first */}
      {filteredTimeeventTags.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            {t('tags_dropdown.custom_tags_header') || 'CUSTOM TAGS'}
          </Typography>
          {filteredTimeeventTags.map((tag, index) => {
            // tag is an object like { key: ..., label: ..., value: ..., description: ... }
            // or could be a simple string if tags prop was an array of strings
            const actualTagValue = getTagValue(tag).trim(); // Correctly gets 'someVar' or the string itself
            const displayLabel = tag?.label || actualTagValue; // Prefer tag.label for display
            const tooltipContent = getTooltipContent(tag, displayLabel);
            
            // Get icon - PRIORITIZE backend-provided icon
            let IconComponent;
            if (tag?.icon) {
              // If icon exists, check if it's a string (from backend) or component
              if (typeof tag.icon === 'string') {
                IconComponent = getIconComponent(tag.icon);
              } else {
                IconComponent = tag.icon;
              }
            } else {
              // Only use auto-generated icon as fallback when no icon is provided from backend
              IconComponent = getCustomTagIcon(tag);
            }

            return (
              <Tooltip
                key={tag?.key || tag?.value || tag?.label || `custom-tag-${index}`}
                title={tooltipContent}
                arrow
                componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }}
                enterDelay={200}
              >
              <MenuItem
                  selected={Boolean(highlightedTagValue && highlightedTagValue.toLowerCase() === actualTagValue.toLowerCase())}
                  onMouseEnter={() => setHighlightedTagValue(actualTagValue)}
                  onClick={() => {
                    handleTagSelect(actualTagValue);
                    setHighlightedTagValue(null);
                    handleClose(); // Close menu on selection
                  }}
                  sx={{
                    px: 2.5, py: 1.2,
                    '&:hover': { backgroundColor: '#f0f0ff', color: '#5757b0' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={displayLabel} // Display user-friendly label or the tag value
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  />
                </MenuItem>
              </Tooltip>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* WIDGET TAGS - Displayed after custom tags */}
      {filteredWidgetTags.length > 0 && (
        <>
          <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
            {t('tags_dropdown.widget_tags_header') || 'WIDGET TAGS'}
          </Typography>
          {filteredWidgetTags.map((tag, index) => {
            const actualTagValue = getTagValue(tag).trim();
            const displayLabel = tag?.label || actualTagValue;
            const tooltipContent = getTooltipContent(tag, displayLabel);
            
            // Get icon - PRIORITIZE backend-provided icon
            let IconComponent;
            if (tag?.icon) {
              if (typeof tag.icon === 'string') {
                IconComponent = getIconComponent(tag.icon);
              } else {
                IconComponent = tag.icon;
              }
            } else {
              // Use widget-specific icon as fallback
              IconComponent = getCustomTagIcon(tag);
            }

            return (
              <Tooltip
                key={tag?.key || tag?.value || tag?.label || `widget-tag-${index}`}
                title={tooltipContent}
                arrow
                componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }}
                enterDelay={200}
              >
                <MenuItem
                  selected={Boolean(highlightedTagValue && highlightedTagValue.toLowerCase() === actualTagValue.toLowerCase())}
                  onMouseEnter={() => setHighlightedTagValue(actualTagValue)}
                  onClick={() => {
                    handleTagSelect(actualTagValue);
                    setHighlightedTagValue(null);
                    handleClose(); // Close menu on selection
                  }}
                  sx={{
                    px: 2.5, py: 1.2,
                    '&:hover': { backgroundColor: '#f0f0ff', color: '#5757b0' },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={displayLabel}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }}
                  />
                </MenuItem>
              </Tooltip>
            );
          })}
          <Divider sx={{ my: 1.5 }} />
        </>
      )}

      {/* DEFAULT TAGS - Grouped for clarity */}
      {filteredDefaultTags.length > 0 && (
        <>
          {/* Project Information Section */}
          {filteredDefaultTags.filter(tag => ['projectid', 'project_title', 'project_description', 'username'].includes(tag.variable || tag.value)).length > 0 && (
            <>
              <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                {t('tags_dropdown.project_info_header') || 'PROJECT INFORMATION'}
              </Typography>
              {filteredDefaultTags
                .filter(tag => ['projectid', 'project_title', 'project_description', 'username'].includes(tag.variable || tag.value))
                .map((tag, index) => {
                  const IconComponent = tag.icon || getCustomTagIcon(tag);
                  const tagValue = tag.variable || tag.value;
                  return (
                    <Tooltip key={tag?.key || tag?.value || tag?.variable || tag?.label || `project-info-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                      <MenuItem
                        selected={Boolean(highlightedTagValue && highlightedTagValue.toLowerCase() === tagValue.toLowerCase())}
                        onMouseEnter={() => setHighlightedTagValue(tagValue)}
                        onClick={() => {
                          handleTagSelect(tagValue);
                          setHighlightedTagValue(null);
                          handleClose(); // Close menu on selection
                        }}
                        sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                        </ListItemIcon>
                        <ListItemText primary={tag.label || tagValue} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                      </MenuItem>
                    </Tooltip>
                  );
                })}
            </>
          )}

          {/* File Metadata Section */}
          {!hideEventTags && filteredDefaultTags.filter(tag => ['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes(tag.value)).length > 0 && (
            <>
              <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                {t('tags_dropdown.file_metadata_header') || 'FILE METADATA'}
              </Typography>
              {filteredDefaultTags
                .filter(tag => ['filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent'].includes(tag.variable || tag.value)) // fileContent is often API specific
                .map((tag, index) => {
                  const IconComponent = tag.icon || getCustomTagIcon(tag);
                  const tagValue = tag.variable || tag.value;
                  return (
                    <Tooltip key={tag?.key || tag?.value || tag?.variable || tag?.label || `file-meta-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                      <MenuItem
                        selected={Boolean(highlightedTagValue && highlightedTagValue.toLowerCase() === tagValue.toLowerCase())}
                        onMouseEnter={() => setHighlightedTagValue(tagValue)}
                        onClick={() => {
                          handleTagSelect(tagValue);
                          setHighlightedTagValue(null);
                          handleClose(); // Close menu on selection
                        }}
                        sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                        </ListItemIcon>
                        <ListItemText primary={tag.label || tagValue} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                      </MenuItem>
                    </Tooltip>
                  );
                })}
            </>
          )}

          {/* Date & Time Section */}
          {filteredDefaultTags.filter(tag => [
            'year', 'month', 'day', 'hour', 'minute', 'second',
            'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
            'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond', // Removed duplicate 'filecreationsecond'
            'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond' // Removed duplicate 'filemodificationsecond'
          ].includes(tag.variable || tag.value)).length > 0 && (
            <React.Fragment key="datetime-tags-section">
              <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                {t('tags_dropdown.datetime_header') || 'DATE & TIME'}
              </Typography>
              {filteredDefaultTags
                .filter(tag => [
                  'year', 'month', 'day', 'hour', 'minute', 'second',
                  'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                  'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond',
                  'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond'
                ].includes(tag.variable || tag.value))
                .map((tag, index) => {
                  const IconComponent = tag.icon || getCustomTagIcon(tag);
                  const tagValue = tag.variable || tag.value;
                  return (
                    <Tooltip key={tag?.key || tag?.value || tag?.variable || tag?.label || `datetime-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                      <MenuItem
                        onClick={() => {
                          handleTagSelect(tagValue);
                          handleClose(); // Close menu on selection
                        }}
                        sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                        </ListItemIcon>
                        <ListItemText primary={tag.label || tagValue} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                      </MenuItem>
                    </Tooltip>
                  );
                })}
            </React.Fragment>
          )}
            {/* Other default tags (e.g. text extract, summary) */}
            {filteredDefaultTags.filter(tag => ![
                'filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent',
                'project_title', 'project_description', 'username',
                'year', 'month', 'day', 'hour', 'minute', 'second',
                'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond',
                'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond'
            ].includes(tag.variable || tag.value)).length > 0 && (
                <React.Fragment key="other-tags-section">
                <Typography variant="caption" sx={{ px: 2.5, py: 1.5, fontWeight: 700, color: '#5757b0', display: 'block', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                    {t('tags_dropdown.other_tags_header') || 'OTHER INFO'}
                </Typography>
                {filteredDefaultTags
                    .filter(tag => ![
                        'filename', 'file_extension', 'file_folder_path', 'fileid', 'projectid', 'fileContent',
                        'project_title', 'project_description', 'username',
                        'year', 'month', 'day', 'hour', 'minute', 'second',
                        'currentyear', 'currentmonth', 'currentday', 'currenthour', 'currentminute', 'currentsecond',
                        'filecreationyear', 'filecreationmonth', 'filecreationday', 'filecreationhour', 'filecreationminute', 'filecreationsecond',
                        'filemodificationyear', 'filemodificationmonth', 'filemodificationday', 'filemodificationhour', 'filemodificationminute', 'filemodificationsecond'
                    ].includes(tag.variable || tag.value))
                    .map((tag, index) => {
                      const IconComponent = tag.icon || getCustomTagIcon(tag);
                      const tagValue = tag.variable || tag.value;
                      return (
                        <Tooltip key={tag?.key || tag?.value || tag?.variable || tag?.label || `other-${index}`} title={tag.description} arrow componentsProps={{ popper: { sx: { pointerEvents: 'none' } } }} enterDelay={200}>
                          <MenuItem
                            selected={Boolean(highlightedTagValue && highlightedTagValue.toLowerCase() === tagValue.toLowerCase())}
                            onMouseEnter={() => setHighlightedTagValue(tagValue)}
                            onClick={() => {
                              handleTagSelect(tagValue);
                              setHighlightedTagValue(null);
                              handleClose(); // Close menu on selection
                            }}
                            sx={{ px: 2.5, py: 1.2, '&:hover': { backgroundColor: '#f0f0ff' }, transition: 'background-color 0.2s ease' }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <IconComponent fontSize="small" sx={{ color: '#5757b0' }} />
                            </ListItemIcon>
                            <ListItemText primary={tag.label || tagValue} primaryTypographyProps={{ fontWeight: 500, fontSize: '0.95rem' }} />
                          </MenuItem>
                        </Tooltip>
                      );
                    })}
                </React.Fragment>
            )}
        </>
      )}
      {filteredTimeeventTags.length === 0 && filteredWidgetTags.length === 0 && filteredDefaultTags.length === 0 && (
        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          {t('tags_dropdown.no_tags_found') || 'No matching tags found. Press Enter to use the tag you typed.'}
        </Typography>
      )}
    </Menu>
  );
};

export default TagsDropdown;
